/**
 * Vorlesen bei ausgeschaltetem Bildschirm — über den nativen Spieler.
 * (24.09.2026, Ticket 2547, ab Build 77)
 *
 * Martin: *„Vespera soll auch im ausgeschalteten Bildschirm weiter vorlesen.“*
 *
 * ## Was das Vorlesen beim Sperren anhielt (Journal 23.09.)
 *
 *     23:19:17  Stück 2 endet — 23:19:31 Seite hidden, es spielte NICHTS
 *     23:19:43  Stück 3 vertont (die Seite rechnete noch)
 *     23:19:44  Stück 3 beginnt im Hintergrund — und kommt nie in Gang
 *     23:21:15  [tts-gap] via=WACHHUND audio=7.27s echt=90.54s
 *
 * Ein `<audio>`, das in der WebView beginnt, während die Seite im
 * Hintergrund liegt, spielt nicht (Albert am 23.09. dasselbe). Die native
 * Hülle hat seit Build 77 zwei Dinge dafür (PawbotVorleser):
 *
 *  - `vorleseHalt`: eine lautlose Wiedergabe hält Tonsitzung und App wach,
 *    solange eine Vorlesung läuft — als Mietvertrag mit Frist, damit eine
 *    eingefrorene Seite nichts ewig festhält.
 *  - `vorleseStueck`: spielt ein Stück nativ, aus den Bytes, die hier
 *    übergeben werden (die `blob:`-Adressen der Seite kennt das Native nicht).
 *
 * ⚠️ Im Vordergrund bleibt es beim Web-Weg — der ist erprobt, und an ihm
 *    hängen Nachspiel, Sonden und die Sprechtasten-Vorrangregel. Nativ
 *    spielt nur, was im Hintergrund beginnt oder dort stehen bleibt
 *    (tts-queue.ts, `playOne`).
 * ⚠️ Ältere App-Fassungen (bis Build 76) kennen diese Methoden nicht. Ob sie
 *    da sind, sagt `Capacitor.PluginHeaders` — ohne sie bleibt alles wie
 *    bisher.
 */
import { loadSettings } from "./settings.js";
import { kontoGruppen } from "./konto-lage.js";
import { vorleserNativAn } from "../../shared/vorleser-nativ.js";
import { getCapacitorPlugin, nativesPluginKennt } from "./native-voice.js";

interface VorleserPlugin {
  vorleseHalt(o: { an: boolean; frist?: number }): Promise<{ haelt?: boolean }>;
  vorleseStueck(o: {
    daten: string;
    ab?: number;
    typ?: string;
  }): Promise<{ grund?: string; dauer?: number }>;
  vorleseStopp(): Promise<void>;
  vorlesePause(o: { an: boolean }): Promise<void>;
  addListener?(
    ereignis: string,
    fn: (d: unknown) => void,
  ): Promise<{ remove(): void | Promise<void> }>;
}

/** So lange gilt der Halt, wenn die Seite ihn nicht verlängert (Sekunden). */
const HALT_FRIST_S = 45;
/** Takt der Hintergrund-Zeile im Journal, solange gehalten wird. */
const PULS_MS = 15_000;
/**
 * Auch die Seite hält nur auf Frist: ohne Verlängerung so lange, dann ist der
 * lautlose Ton aus — eine Warteschlange, die nie fertig meldet, darf ihn
 * nicht ewig laufen lassen (Akku). Das Native hat seine eigene Frist.
 */
const SEITEN_FRIST_MS = 60_000;

/**
 * ⚠️⚠️ 24.09.2026, 21:1x — DER NATIVE VORLESE-WEG IST ABGESCHALTET.
 *    Protokoll 21:10:25–28 (Build 80, pre.3281): Seite verborgen, danach
 *    scheiterte JEDES Stück im nativen Spieler sofort („nativ:fehler:play“
 *    nach 0,13 s) — der ganze Rest der Antwort war in einer Sekunde
 *    übersprungen. Martin: „Echt eine Katastrophe.“ Bis der Weg am Gerät
 *    mit echtem Ton belegt ist, spielt die Seite wie vor Build 77.
 */
let testFreigabe: boolean | null = null;

/**
 * 13.10.2026: Der Weg bleibt AUS, außer das Konto hat den Testschalter „Antworten im Hintergrund vorlesen“ gesetzt
 * (Einstellungen → Stimme und Vorlesen, `vorleserNativ`, Vorgabe aus).
 *
 * ⚠️ Hier wird NUR der Schalter geprüft, nicht die Sichtbarkeit: Steuerbefehle (Pause, Halt-Freigabe, Fernbedienung)
 *    müssen ein laufendes natives Stück auch erreichen, wenn die Seite wieder vorn ist. Dass ein NEUER Start nur bei
 *    verborgener Seite nativ läuft, prüfen die Startstellen in tts-queue.ts selbst (`visibilityState === "hidden"`).
 */
export function nativerVorleserAn(): boolean {
  if (testFreigabe !== null) return testFreigabe;
  try {
    // 20.10.2026: der Testschalter steht nur im Konto 1 zur Wahl — in anderen Konten gilt er als aus.
    return kontoGruppen().versuche && vorleserNativAn(loadSettings().vorleserNativ);
  } catch {
    return false;
  }
}

/** Nur für Tests: den Weg fest freigeben/sperren (ohne Argument: wieder die Einstellung gelten lassen). */
export function _nativenVorleserFuerTestFreigeben(an: boolean | null = null): void {
  testFreigabe = an;
}

/** Kennt die installierte App (ab Build 77) den nativen Vorleser — und ist er freigegeben? */
export function nativerVorleserDa(): boolean {
  if (!nativerVorleserAn()) return false;
  return nativesPluginKennt("PawbotRecorder", "vorleseStueck");
}

function vorleser(): VorleserPlugin | null {
  return nativerVorleserDa() ? getCapacitorPlugin<VorleserPlugin>("PawbotRecorder") : null;
}

/** Nur fürs Protokoll: ein Fehler als ein Wort. */
function kurz(e: unknown): string {
  const t = e instanceof Error ? e.name || e.message : String(e);
  return t.replace(/\s+/g, "-").slice(0, 40) || "unbekannt";
}

/** Eine Zeile ins Journal — die Warteschlange reicht ihre `meldeZeile` durch. */
export type Melde = (zeile: string) => void;
let melde: Melde | null = null;
function zeile(z: string): void {
  try {
    melde?.(z);
  } catch {
    /* ein Protokoll darf nie die Wiedergabe stören */
  }
}

/*
 * Nachprüfung Build 77 — WER HÄLT.
 *
 * Bis zur Nachprüfung war der Halt EIN Merker für alle Warteschlangen
 * (app.ts hält mehrere, `activeTtsQueues`). Eine abgebrochene alte
 * Warteschlange ruft `resolveIdle()` zweimal — in cancel() und noch einmal
 * verspätet im `finally` ihrer Abspielschleife. Kam der späte Aufruf nach dem
 * Start der neuen, gab er DEREN Halt frei, und nativ endete sogar ihr gerade
 * spielendes Stück („seite" → abbruch). Jetzt hält jede Warteschlange für
 * sich; frei wird erst, wenn keine mehr hält. Ein doppeltes Freigeben
 * derselben ist wirkungslos.
 */
const halter = new Set<object>();
let fristUhr: ReturnType<typeof setTimeout> | undefined;

function fristNeu(): void {
  if (fristUhr !== undefined) clearTimeout(fristUhr);
  fristUhr = setTimeout(() => {
    fristUhr = undefined;
    if (halter.size === 0) return;
    halter.clear();
    haltEnde("frist");
  }, SEITEN_FRIST_MS);
}

/**
 * Die Tonsitzung für die Dauer einer Vorlesung halten — oder freigeben.
 * Jedes `true` verlängert die Frist. `besitzer` ist die Warteschlange.
 * Außerhalb der App und bei alten App-Fassungen: nichts.
 */
export function vorleseHalt(an: boolean, besitzer: object, protokoll?: Melde): void {
  const p = vorleser();
  if (!p) return;
  if (protokoll) melde = protokoll;
  if (an) {
    const erster = halter.size === 0;
    halter.add(besitzer);
    wachTonHalten();
    fristNeu();
    if (erster) sichtbarkeitBeobachten();
    void p.vorleseHalt({ an: true, frist: HALT_FRIST_S }).catch(() => undefined);
    return;
  }
  if (!halter.delete(besitzer) || halter.size > 0) return;
  haltEnde("frei");
  void p.vorleseHalt({ an: false }).catch(() => undefined);
}

/**
 * Beim Start der Seite: einen Halt, den eine frühere Seite (vor dem Neuladen)
 * hinterließ, ausdrücklich freigeben — ohne Kurzschluss, denn diese Seite
 * weiß von keinem. Sonst wartete der Wach-Wort-Lauscher bis zur Frist
 * (45 s) mit „vorlesen". (Nachprüfung Build 77)
 */
export function vorleseHaltZuruecksetzen(): void {
  const p = vorleser();
  if (!p) return;
  halter.clear();
  haltEnde("start");
  void p.vorleseHalt({ an: false }).catch(() => undefined);
}

/*
 * Nachprüfung Build 77 — DER LAUTLOSE TON IN DER WEBVIEW.
 *
 * Die Prüfung fand die Stelle, an der Ticket 2547 hängt: Stillstand
 * erkennen, das nächste Stück holen und `vorleseStueck` rufen — das tut
 * diese Seite, und ob ihr JavaScript bei gesperrtem Bildschirm weiterläuft,
 * ist nicht belegt (tts-queue.ts, 19.07.: „Im HINTERGRUND friert iOS die
 * Timer der WebView ein"). Die lautlose Wiedergabe im Nativen hält nur den
 * App-Prozess wach, nicht den WebContent-Prozess von WebKit.
 *
 * WebKit hält diesen Prozess wach, solange die Seite hörbar Medien spielt.
 * Darum spielt, solange eine Vorlesung gehalten wird, ein lautloser,
 * geloopter Ton HIER — begonnen vorn (ein `<audio>`, das im Hintergrund
 * beginnt, kommt nicht in Gang), geendet mit dem Halt. Stille aus Nullen,
 * nicht `muted`: ein stummgeschaltetes Element zählt für WebKit nicht als
 * „spielt". Kein Mikrofon, keine Anzeige, nicht im Dokument.
 *
 * ⚠️ Ob WebKit das so wertet, lässt sich hier nicht messen. Das Journal
 *    zeigt es am Gerät: `[vorlesen] hintergrund-puls` kommt alle 15 s,
 *    solange die Seite im Hintergrund läuft — reißt der Puls ab, war sie
 *    angehalten.
 */
let wachTon: HTMLAudioElement | null = null;
let stilleAdresse = "";

/** Eine halbe Sekunde Stille als WAV (16 bit, 8 kHz, mono), als data:-Adresse. */
export function stillerWav(): string {
  if (stilleAdresse) return stilleAdresse;
  const rate = 8000;
  const frames = 4000;
  const b = new Uint8Array(44 + frames * 2);
  const v = new DataView(b.buffer);
  const text = (ort: number, s: string): void => {
    for (let i = 0; i < s.length; i++) b[ort + i] = s.charCodeAt(i);
  };
  text(0, "RIFF");
  v.setUint32(4, 36 + frames * 2, true);
  text(8, "WAVE");
  text(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  text(36, "data");
  v.setUint32(40, frames * 2, true);
  stilleAdresse = `data:audio/wav;base64,${alsBase64(b)}`;
  return stilleAdresse;
}

function wachTonZustand(): string {
  if (!wachTon) return "aus";
  return wachTon.paused ? "steht" : "spielt";
}

/**
 * ⚠️⚠️ 24.09.2026, 21:1x — ABGESCHALTET. Martin: „Echt eine Katastrophe.
 *    Fällt die ganze Zeit aus.“ Seit pre.3273 scheiterten Stücke, vor denen
 *    „wachton an“ stand, zu 44 % (7 von 16), ohne ihn zu 9 % (10 von 106):
 *    ein zweites <audio>, das in WKWebView zu spielen beginnt, pausiert das
 *    laufende Vorlese-Stück. Bis das am Gerät anders belegt ist, spielt die
 *    Seite keinen Wachton; das Vorlesen im Hintergrund trägt allein der
 *    native Halt (vorleseHalt).
 */
const WACHTON_AN = false;

/** Den lautlosen Ton anwerfen — oder wieder, falls WebKit ihn anhielt. */
function wachTonHalten(): void {
  if (!WACHTON_AN) return;
  try {
    if (!wachTon) {
      const a = document.createElement("audio");
      a.src = stillerWav();
      a.loop = true;
      a.preload = "auto";
      wachTon = a;
    }
    if (!wachTon.paused) return;
    void Promise.resolve(wachTon.play()).then(
      () => zeile(`[vorlesen] wachton an sichtbar=${document.visibilityState}`),
      (e: unknown) => zeile(`[vorlesen] wachton abgelehnt: ${kurz(e)}`),
    );
  } catch (e) {
    zeile(`[vorlesen] wachton fehler: ${kurz(e)}`);
  }
}

/*
 * Der Beleg fürs Gerät: solange gehalten wird und die Seite verborgen ist,
 * alle 15 s eine Zeile. Beim Zurückkommen oder am Ende der Vorlesung eine
 * Zusammenfassung — wie lange verborgen, wie viele Pulse. Ein Puls je 15 s
 * heißt: die Seite lief durch.
 */
let beobachtet = false;
let puls: ReturnType<typeof setInterval> | undefined;
let verborgenSeit = 0;
let pulse = 0;

function sekunden(): number {
  return Math.round((Date.now() - verborgenSeit) / 1000);
}

function pulsStarten(): void {
  if (puls !== undefined) return;
  verborgenSeit = Date.now();
  pulse = 0;
  zeile(`[vorlesen] verborgen wachton=${wachTonZustand()}`);
  puls = setInterval(() => {
    pulse += 1;
    zeile(`[vorlesen] hintergrund-puls ${sekunden()}s wachton=${wachTonZustand()}`);
  }, PULS_MS);
}

function pulsBeenden(grund: string): void {
  if (puls === undefined) return;
  clearInterval(puls);
  puls = undefined;
  zeile(`[vorlesen] ${grund} nach ${sekunden()}s im hintergrund, puls=${pulse}`);
}

function sichtbarkeitBeobachten(): void {
  if (document.visibilityState === "hidden") pulsStarten();
  if (beobachtet) return;
  beobachtet = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      if (halter.size > 0) pulsStarten();
    } else {
      pulsBeenden("sichtbar");
      // Hielt WebKit den Ton im Hintergrund an, spielt er vorn wieder.
      if (halter.size > 0) wachTonHalten();
    }
  });
}

function haltEnde(grund: string): void {
  if (fristUhr !== undefined) clearTimeout(fristUhr);
  fristUhr = undefined;
  pulsBeenden(grund === "frei" ? "vorlesung aus" : grund);
  try {
    wachTon?.pause();
  } catch {
    /* egal */
  }
}

/** Bytes als base64 — in Happen, damit große Stücke den Stapel nicht sprengen. */
export function alsBase64(bytes: Uint8Array): string {
  let binaer = "";
  const HAPPEN = 0x8000;
  for (let i = 0; i < bytes.length; i += HAPPEN) {
    binaer += String.fromCharCode(...bytes.subarray(i, i + HAPPEN));
  }
  return btoa(binaer);
}

/**
 * Ein Stück nativ spielen. Löst mit dem Grund, warum es endete:
 * `ende`, `abbruch`, `abgeloest`, `frist` oder `fehler:…`.
 *
 * ⚠️ `quelle` ist eine `blob:`-Adresse oder eine Adresse dieses Servers —
 *    die Bytes holt die Seite (mit ihrer Sitzung), das Native bekommt nur
 *    den Inhalt, keine Adresse. Dazu die Art (Content-Type), damit der
 *    Spieler rohe MP3-Rahmen sicher erkennt (Nachprüfung Build 77).
 */
export async function spieleNativ(
  quelle: string,
  opts: { ab?: number; signal: AbortSignal; holen?: typeof fetch },
): Promise<string> {
  const p = vorleser();
  if (!p) return "fehler:kein-vorleser";
  let daten: string;
  let typ: string;
  try {
    const antwort = await (opts.holen ?? fetch)(quelle, { credentials: "same-origin" });
    if (!antwort.ok) return `fehler:laden-${antwort.status}`;
    typ = antwort.headers.get("content-type") ?? "";
    daten = alsBase64(new Uint8Array(await antwort.arrayBuffer()));
  } catch (e) {
    return `fehler:laden-${kurz(e)}`;
  }
  if (opts.signal.aborted) return "abbruch";
  const stopp = (): void => {
    void p.vorleseStopp().catch(() => undefined);
  };
  opts.signal.addEventListener("abort", stopp, { once: true });
  try {
    const r = await p.vorleseStueck({ daten, ab: opts.ab ?? 0, typ });
    return r?.grund ?? "ende";
  } catch (e) {
    return `fehler:${kurz(e)}`;
  } finally {
    opts.signal.removeEventListener("abort", stopp);
  }
}

/** Nutzer-Pause für ein nativ spielendes Stück. */
export function pausiereNativ(an: boolean): void {
  void vorleser()?.vorlesePause({ an }).catch(() => undefined);
}

export type FernBefehl = "anhalten" | "fortsetzen";

/**
 * Pause/Fortsetzen vom Sperrbildschirm. (Nachprüfung Build 77)
 *
 * Ab Build 77 sagt die App, WAS gedrückt wurde (`befehl`) und ob ein nativ
 * spielendes Stück es schon ausgeführt hat (`nativ`) — die Seite gleicht
 * dann nur ihre Warteschlange an. Bis Build 76 kam das Ereignis ohne Inhalt,
 * und niemand hörte darauf; für diese Fassungen bleibt es dabei.
 */
export async function aufFernbedienungHoeren(
  cb: (befehl: FernBefehl, nativ: boolean) => void,
): Promise<(() => void) | null> {
  const p = vorleser();
  if (!p?.addListener) return null;
  try {
    const h = await p.addListener("wiedergabeUmschalten", (d) => {
      const daten = (d ?? {}) as { befehl?: unknown; nativ?: unknown };
      if (daten.befehl !== "anhalten" && daten.befehl !== "fortsetzen") return;
      cb(daten.befehl, daten.nativ === true);
    });
    return () => void h.remove();
  } catch {
    return null;
  }
}

/** Nur für Tests: Halter, Ton, Puls und Protokoll vergessen. */
export function _vorlesenNativVergessen(): void {
  halter.clear();
  if (fristUhr !== undefined) clearTimeout(fristUhr);
  fristUhr = undefined;
  if (puls !== undefined) clearInterval(puls);
  puls = undefined;
  wachTon = null;
  melde = null;
}
